import { apiService } from './apiService';

export const driverService = {
  // Get all drivers
  async getDrivers(params?: { type?: string; status?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    
    const query = queryParams.toString();
    return await apiService.get(`/drivers${query ? `?${query}` : ''}`);
  },

  // Get driver by ID
  async getDriver(id: string | number) {
    return await apiService.get(`/drivers/${id}`);
  },

  async getDriverByUserId(userId: string | number) {
    return await apiService.get(`/drivers/user/${userId}`);
  },

  async bulkSendMessage(data: { driverIds: Array<string | number>; message: string; channel: 'sms' | 'whatsapp'; targetType?: 'internal' | 'external' }) {
    return await apiService.post('/drivers/bulk-message', data);
  },

  // Create driver
  async createDriver(data: any) {
    return await apiService.post('/drivers', data);
  },

  // Update driver
  async updateDriver(id: string | number, data: any) {
    return await apiService.put(`/drivers/${id}`, data);
  },

  // Delete driver
  async deleteDriver(id: string | number) {
    return await apiService.delete(`/drivers/${id}`);
  },

  // Get driver applications
  async getApplications(params?: { status?: string; page?: number; pageSize?: number; all?: boolean }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.pageSize) queryParams.append('pageSize', String(params.pageSize));
    if (params?.all) queryParams.append('all', '1');
    
    const query = queryParams.toString();
    return await apiService.get(`/applications${query ? `?${query}` : ''}`);
  },

  async getMyApplication() {
    return await apiService.get('/applications/self');
  },

  async updateMyApplication(data: any) {
    return await apiService.put('/applications/self', data);
  },

  async updateApplication(id: string | number, data: any) {
    return await apiService.put(`/applications/${id}`, data);
  },

  async updateApplicationManualLocation(id: string | number, data: { latitude: number; longitude: number; recordedAt?: string; locationDescription?: string }) {
    return await apiService.put(`/applications/${id}/manual-location`, data);
  },

  async createApplication(data: any) {
    return await apiService.post('/applications', data);
  },

  async deleteApplication(id: string | number) {
    return await apiService.delete(`/applications/${id}`);
  },

  async syncExternalDriversFromWorkiom() {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${baseUrl}/workiom/sync-external-drivers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(300000),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Workiom sürücü senkronizasyonu başarısız');
    }
    return data;
  },

  async syncInternalDriversFromWorkiom() {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${baseUrl}/workiom/sync-internal-drivers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(300000),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Workiom özmal sürücü senkronizasyonu başarısız');
    }
    return data;
  },

  async syncInternalDriverVehicleInfo() {
    return apiService.post('/drivers/sync-internal-vehicle-info', {});
  },

  async fillResidenceCoordinates() {
    return apiService.post('/drivers/fill-residence-coordinates', {});
  },

  async syncInternalDriversFromSeyir() {
    return apiService.post('/drivers/sync-seyir-data', {});
  },

  async getSeyirDriverComparisons() {
    return apiService.get('/drivers/seyir-driver-comparisons');
  },

  async getInternalHarcirahReport(params: { startDate: string; endDate: string }) {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    return await apiService.get(`/drivers/internal-harcirah-report?${queryParams.toString()}`);
  },

  async getInternalHarcirahKmReport(params: { startDate: string; endDate: string }) {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    return await apiService.get(`/drivers/internal-harcirah-km-report?${queryParams.toString()}`, { timeoutMs: 900000 });
  },

  async pushExternalDriversToWorkiom(applicationIds: Array<string | number>) {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${baseUrl}/workiom/push-external-drivers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ applicationIds }),
      signal: AbortSignal.timeout(300000),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Workiom sürücü gönderimi başarısız');
    }
    return data;
  },

  // Update application status
  async updateApplicationStatus(id: string | number, status: string, notes?: string, reviewedBy?: string | number) {
    if (status === 'approved') {
      return await apiService.put(`/applications/${id}/approve`, { reviewedBy: reviewedBy || null });
    }
    if (status === 'rejected') {
      return await apiService.put(`/applications/${id}/reject`, { reviewedBy: reviewedBy || null, notes: notes || 'Basvuru reddedildi' });
    }
    return await apiService.put(`/applications/${id}`, { status, notes, reviewedBy: reviewedBy || null });
  },

  // Approve all pending applications
  async approveAllApplications(reviewedBy?: string | number) {
    return await apiService.put(`/applications/approve-all`, { reviewedBy: reviewedBy || null });
  },

  // Get driver leave requests
  async getLeaveRequests(params?: { status?: string; driverId?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.driverId) queryParams.append('driverId', params.driverId);
    
    const query = queryParams.toString();
    return await apiService.get(`/leave-requests${query ? `?${query}` : ''}`);
  },

  // Create leave request
  async createLeaveRequest(data: any) {
    return await apiService.post('/leave-requests', data);
  },

  // Update leave request
  async updateLeaveRequest(id: string | number, data: any) {
    return await apiService.put(`/leave-requests/${id}`, data);
  },

  // Approve leave request
  async approveLeaveRequest(id: string | number, approvedBy: string | number, notes?: string) {
    return await apiService.put(`/leave-requests/${id}/approve`, { approvedBy, notes });
  },

  // Reject leave request
  async rejectLeaveRequest(id: string | number, approvedBy: string | number, rejectionReason?: string) {
    return await apiService.put(`/leave-requests/${id}/reject`, { approvedBy, rejectionReason });
  },

  // Delete leave request
  async deleteLeaveRequest(id: string | number) {
    return await apiService.delete(`/leave-requests/${id}`);
  },

  async getLeaveBalances() {
    return await apiService.get('/leave-requests/balances');
  },

  async getDriverLeaveBalance(driverId: string | number) {
    return await apiService.get(`/leave-requests/balances/${driverId}`);
  },

  async createLeaveBalanceTransaction(data: any) {
    return await apiService.post('/leave-requests/balances/transactions', data);
  },

  async importLeaveBalances(rows: any[]) {
    return await apiService.post('/leave-requests/balances/import', { rows });
  },

  async getActiveDriverCalendar(year: number, month: number) {
    return await apiService.get(`/leave-requests/calendar/active-drivers?year=${year}&month=${month}`);
  },

  async getAllLeaveBalanceTransactions(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString();
    return await apiService.get(`/leave-requests/transactions/all${qs ? `?${qs}` : ''}`);
  },

  // Get driver earnings
  async getEarnings(driverId: string | number, params?: { startDate?: string; endDate?: string }) {
    const queryParams = new URLSearchParams();
    queryParams.append('driverId', String(driverId));
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const query = queryParams.toString();
    return await apiService.get(`/earnings${query ? `?${query}` : ''}`);
  },

  async getEarningsSummary(driverId: string | number) {
    return await apiService.get(`/earnings/driver/${driverId}/summary`);
  },
};
